import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Rate } from '../../../shared/types/Rate';
import { MoreHorizontalIcon, PlusIcon, GripVerticalIcon } from 'lucide-react';
export function Rates() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data based on the screenshot
  const [rates, setRates] = useState<Rate[]>([{
    id: '1',
    name: 'Test With Scuba Spa',
    description: 'Scuba spa test',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES',
    baseNightlyRate: 100.0,
    cancellationPlan: 'NON_REFUNDABLE',
    status: 'inactive',
    isPromo: true
  }, {
    id: '2',
    name: 'Advance Booking with Free Seaplan Transfer',
    description: 'Plan your Langkawi escape early and enjoy the added luxury of a complimentary round-trip ferry transfer from Kuala Kedah to the resort. This exclusive offer rewards advance planners with significant savings on transportation costs.',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES2025',
    baseNightlyRate: 100.0,
    cancellationPlan: 'FLEXIBLE',
    status: 'active',
    isPromo: true
  }]);
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roomTypeFilter, setRoomTypeFilter] = useState<string>('all');
  // Filter rates based on search term and filters
  const filteredRates = rates.filter(rate => {
    const matchesSearch = rate.name.toLowerCase().includes(searchTerm.toLowerCase()) || rate.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || statusFilter === 'active' && rate.status === 'active' || statusFilter === 'inactive' && rate.status === 'inactive';
    const matchesRoomType = roomTypeFilter === 'all' || rate.roomType === roomTypeFilter;
    return matchesSearch && matchesStatus && matchesRoomType;
  });
  // Format currency for display
  function formatCurrency(amount: number): string {
    return formatCurrencyAmount(amount, {
      fractionDigits: 2
    });
  }
  // Format cancellation plan for display
  function formatCancellationPlan(plan: string): string {
    return plan.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }
  return <div className="space-y-6" data-id="element-10191" data-mp="3uqgmn8keragc">
      <div className="flex justify-between items-center" data-id="element-10192" data-mp="3uri2dqcdcl4e">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-10193" data-mp="3py6sfgc9fo89">
          {t('pages.Rates.rates')}
        </h1>
        <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center" data-id="element-10194" data-mp="3l0dlum71os7g">
          <PlusIcon size={18} className="mr-1" data-id="element-10195" data-mp="3g2lx211p5m7h" />
          {t('pages.Rates.add_rates')}
        </button>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-id="element-10196" data-mp="370cmjb6fhsu3">
        <input type="search" aria-label={t('pages.Rates.attrs.search_rates')} placeholder={t('pages.Rates.attrs.search_rates')} className="border border-gray-300 rounded-md px-3 py-2 w-48" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-10197" data-mp="371dx7ejj8dba" />

        <select className="border border-gray-300 rounded-md px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-10198" data-mp="1vgnhn1jdgvnd">
          <option value="all" data-id="element-10199" data-mp="12vw0trgmrnx3">{t('components.AddRoomForm.status')}</option>
          <option value="active" data-id="element-10200" data-mp="17u79vzz3pue9">{t('pages.Rates.active')}</option>
          <option value="inactive" data-id="element-10201" data-mp="1csiiy8hko0vf">{t('pages.Rates.inactive')}</option>
        </select>
        <select className="border border-gray-300 rounded-md px-3 py-2" value={roomTypeFilter} onChange={e => setRoomTypeFilter(e.target.value)} data-id="element-10202" data-mp="1hiywrtpb1oib">
          <option value="all" data-id="element-10203" data-mp="k1qrtpfd62ey">{t('pages.Rates.room_type')}</option>
          <option value="DELUXE" data-id="element-10204" data-mp="p1d2ps7pz6kl">{t('pages.Rates.deluxe')}</option>
          <option value="SUPERIOR" data-id="element-10205" data-mp="2qrbzl5a907w4">{t('pages.Rates.superior')}</option>
          <option value="OVERWATER_VILLA" data-id="element-10206" data-mp="2qphg7vemknu5">
            {t('pages.Rates.overwater_villa')}
          </option>
        </select>
      </div>
      {/* Rates List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-10207" data-mp="30j7xogcc3y0c">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-10208" data-mp="2vpvkd2d3vdmw">
          <thead className="bg-gray-50" data-id="element-10209" data-mp="3afkpf8obk1sg">
            <tr data-id="element-10210" data-mp="3aehzb2ytm3dl">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10211" data-mp="3adzwtolkesx8">
                {t('pages.Rates.details')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10212" data-mp="3ehem75ve8lnc">
                {t('pages.Rates.promo_code')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10213" data-mp="1k4ggz0ojwf6v">
                {t('pages.Rates.base_nightly_rate')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10214" data-mp="4mjujtz7icnw">
                {t('pages.Rates.cancellation_plan')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10215" data-mp="3czpqseln08sr">
                {t('components.AddRoomForm.status')}
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10216" data-mp="3415fvzmbj3f3">
                {t('components.RoomAssets.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-10217" data-mp="168rqtq7mecp9">
            {filteredRates.map(rate => <tr key={rate.id} data-id="element-10218" data-mp="2f7j1zllqm24z">
                <td className="px-6 py-4" data-id="element-10219" data-mp="2f4dda7h0t29s">
                  <div className="flex items-start" data-id="element-10220" data-mp="2a7dhcpdvxfwe">
                    <GripVerticalIcon size={16} className="text-gray-400 mt-1 mr-2" data-id="element-10221" data-mp="2a9zq2scii1g7" />

                    <div data-id="element-10222" data-mp="21808nyxl4vcq">
                      <div className="text-sm font-medium text-gray-900" data-id="element-10223" data-mp="218t629anm8i7">
                        {rate.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 max-w-lg" data-id="element-10224" data-mp="2tvmdtbmgo81p">
                        {rate.description}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10225" data-mp="2k6ck5rbyyhbh">
                  <div className="text-sm text-gray-900" data-id="element-10226" data-mp="2k3q8bfi1pfng">{rate.promoCode}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10227" data-mp="3hnb5cprgkshl">
                  <div className="text-sm text-gray-900" data-id="element-10228" data-mp="3ctq6kvi1juhc">
                    {formatCurrency(rate.baseNightlyRate)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10229" data-mp="3p4hhpf0e9xsf">
                  <div className="text-sm text-gray-900" data-id="element-10230" data-mp="3p4zmj014hkby">
                    {formatCancellationPlan(rate.cancellationPlan)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10231" data-mp="212wj1tjif1c">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${rate.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10232" data-mp="6ze93kb7z3n9">
                    {rate.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                  {rate.isPromo && <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-10233" data-mp="ln7mcycu1u2o">
                      {t('pages.Rates.promo')}
                    </span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-10234" data-mp="1fz4dvq4sng7b">
                  <button aria-label="Rate actions" className="text-gray-400 hover:text-gray-500" data-id="element-10235" data-mp="1g1gv212q9mjb">
                    <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-10236" data-mp="1pwild08qounh" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}